import React from 'react';
import {
  Typography,
  Box,
  Container,
  Grid,
  Paper,
  useMediaQuery,
  useTheme,
  Button,
} from "@mui/material";
import {
  QrCodeScanner as QrCodeScannerIcon,
  MenuBook as MenuBookIcon,
  ShoppingCart as ShoppingCartIcon,
  Restaurant as RestaurantIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";

const HowItWorks = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const steps = [
    {
      id: 1,
      icon: <QrCodeScannerIcon sx={{ fontSize: { xs: 40, md: 48 } }} />,
      title: "Customer QR Scan",
      description: "Customer scans the QR code placed on the table using their phone camera",
      bgColor: "var(--bg-primary-soft)",
      iconColor: "var(--primary)",
    },
    {
      id: 2,
      icon: <MenuBookIcon sx={{ fontSize: { xs: 40, md: 48 } }} />,
      title: "Menu Opens",
      description: "Digital menu opens instantly with all categories, items & prices",
      bgColor: "var(--bg-light)",
      iconColor: "var(--primary-dark)",
    },
    {
      id: 3,
      icon: <ShoppingCartIcon sx={{ fontSize: { xs: 40, md: 48 } }} />,
      title: "Place Order",
      description: "Customer adds items to cart and places the order instantly",
      bgColor: "var(--bg-orange)",
      iconColor: "var(--primary)",
    },
    {
      id: 4,
      icon: <RestaurantIcon sx={{ fontSize: { xs: 40, md: 48 } }} />,
      title: "Restaurant Receives",
      description: "Order instantly appears on dashboard, kitchen screen & WhatsApp",
      bgColor: "rgba(255, 107, 0, 0.12)",
      iconColor: "var(--primary-dark)",
    },
  ];

  return (
    <Box
      // sx={{
      //   py: { xs: 6, sm: 8, md: 12 },
      //   bgcolor: "#ffffff",
      // }}

      sx={{
        py: { xs: 6, sm: 8, md: 10 },
        bgcolor: "var(--bg-light)",
        borderTop: "1px solid var(--border)",
        borderBottom: "1px solid var(--border)",
      }}
    >
      <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
        
        {/* Heading */}
        <Box sx={{ textAlign: "center", mb: { xs: 4, md: 6 } }}>
          <Typography
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1.5,
              color: "var(--primary)",
              fontWeight: 700,
              fontSize: { xs: "13px", sm: "14px", md: "14px" },
              letterSpacing: "2px",
              textTransform: "uppercase",
              mb: 1,
            }}
          >
            <Box
              sx={{
                width: 30,
                height: 2,
                bgcolor: "var(--primary)",
                borderRadius: "2px",
              }}
            />
            How It Works
            <Box
              sx={{
                width: 30,
                height: 2,
                bgcolor: "var(--primary)",
                borderRadius: "2px",
              }}
            />
          </Typography>

          <Typography
            sx={{
              fontWeight: 900,
              fontSize: { xs: "2rem", sm: "2.8rem", md: "2.8rem" },
              color: "var(--text)",
              lineHeight: 1.2,
              mb: 1,
            }}
          >
            From{" "}
            <Box component="span" sx={{ color: "var(--primary)" }}>
              Scan
            </Box>{" "}
            to{" "}
            <Box component="span" sx={{ color: "var(--primary)" }}>
              Order
            </Box>{" "}
            in 4 Simple Steps
          </Typography>

          <Typography
            sx={{
              color: "var(--text-light)",
              fontSize: { xs: "15px", sm: "16px", md: "16px" },
              maxWidth: "600px",
              mx: "auto",
              lineHeight: 1.6,
            }}
          >
            Seamless ordering experience from customer scan to restaurant delivery
          </Typography>
        </Box>

        {/* Steps Grid - Using xs={12} sm={6} md={3} */}
        <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
          {steps.map((step, index) => (
            <Grid item size={{xs:12, sm:6, md:3 }} key={step.id}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, sm: 3.5, md: 4 },
                  borderRadius: "20px",
                  textAlign: "center",
                  bgcolor: "#ffffff",
                  border: "1px solid var(--border)",
                  transition: "all 0.4s ease",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  position: "relative",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: "var(--shadow-orange)",
                    borderColor: "var(--primary)",
                  },
                }}
              >
                {/* Step Number Badge */}
                <Box
                  sx={{
                    position: "absolute",
                    top: -12,
                    right: -12,
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    bgcolor: "var(--primary)",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "14px",
                    fontWeight: 900,
                    boxShadow: "var(--shadow-orange)",
                  }}
                >
                  {step.id}
                </Box>

                {/* Icon Circle */}
                <Box
                  sx={{
                    width: { xs: 80, sm: 88, md: 96 },
                    height: { xs: 80, sm: 88, md: 96 },
                    borderRadius: "50%",
                    bgcolor: step.bgColor,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: step.iconColor,
                    mb: { xs: 2, md: 2.5 },
                    transition: "all 0.4s ease",
                    "&:hover": {
                      transform: "scale(1.1) rotate(-8deg)",
                      boxShadow: `0 8px 30px rgba(255, 107, 0, 0.15)`,
                    },
                  }}
                >
                  {step.icon}
                </Box>

                {/* Title */}
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: "1rem", sm: "1.1rem", md: "1.2rem" },
                    color: "var(--text)",
                    mb: 1,
                    lineHeight: 1.3,
                  }}
                >
                  {step.title}
                </Typography>

                {/* Description */}
                <Typography
                  sx={{
                    color: "var(--text-light)",
                    fontSize: { xs: "13px", sm: "14px", md: "14px" },
                    lineHeight: 1.6,
                    flex: 1,
                  }}
                >
                  {step.description}
                </Typography>

                {/* Small arrow indicator on hover */}
                <Box
                  sx={{
                    mt: 2,
                    color: "var(--primary)",
                    opacity: 0,
                    transform: "translateX(-10px)",
                    transition: "all 0.3s ease",
                    ".MuiPaper-root:hover &": {
                      opacity: 1,
                      transform: "translateX(0)",
                    },
                  }}
                >
                  <ArrowForwardIcon sx={{ fontSize: 20 }} />
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Flow Indicator - Desktop */}
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            justifyContent: "center",
            alignItems: "center",
            gap: 2,
            mt: 3,
            mb: 4,
          }}
        >
          {steps.map((step, index) => (
            <React.Fragment key={step.id}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  bgcolor: "var(--primary)",
                  opacity: 0.6,
                }}
              />
              {index < steps.length - 1 && (
                <Box
                  sx={{
                    width: 40,
                    height: 2,
                    bgcolor: "var(--border)",
                    borderRadius: "2px",
                    position: "relative",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      right: -4,
                      top: -3,
                      width: 0,
                      height: 0,
                      borderLeft: "6px solid var(--border)",
                      borderTop: "4px solid transparent",
                      borderBottom: "4px solid transparent",
                    },
                  }}
                />
              )}
            </React.Fragment>
          ))}
        </Box>

        {/* Stats Section */}
        <Box
          sx={{
            mt: { xs: 4, md: 5 },
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
            gap: { xs: 2, sm: 3 },
            p: { xs: 3, sm: 4, md: 5 },
            bgcolor: "var(--bg-light)",
            borderRadius: "20px",
            border: "1px solid var(--border)",
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <Typography sx={{ fontSize: { xs: "2rem", md: "2.5rem" }, mb: 0.5 }}>
              ⚡
            </Typography>
            <Typography sx={{ fontWeight: 700, color: "var(--text)", fontSize: { xs: "1rem", md: "1.1rem" } }}>
              Under 2 Minutes
            </Typography>
            <Typography sx={{ color: "var(--text-light)", fontSize: { xs: "13px", md: "14px" } }}>
              Average order time
            </Typography>
          </Box>

          <Box sx={{ textAlign: "center" }}>
            <Typography sx={{ fontSize: { xs: "2rem", md: "2.5rem" }, mb: 0.5 }}>
              📱
            </Typography>
            <Typography sx={{ fontWeight: 700, color: "var(--text)", fontSize: { xs: "1rem", md: "1.1rem" } }}>
              No App Required
            </Typography>
            <Typography sx={{ color: "var(--text-light)", fontSize: { xs: "13px", md: "14px" } }}>
              Just scan & order
            </Typography>
          </Box>

          <Box sx={{ textAlign: "center" }}>
            <Typography sx={{ fontSize: { xs: "2rem", md: "2.5rem" }, mb: 0.5 }}>
              ✅
            </Typography>
            <Typography sx={{ fontWeight: 700, color: "var(--text)", fontSize: { xs: "1rem", md: "1.1rem" } }}>
              99.9% Accuracy
            </Typography>
            <Typography sx={{ color: "var(--text-light)", fontSize: { xs: "13px", md: "14px" } }}>
              Error-free ordering
            </Typography>
          </Box>
        </Box>

        {/* CTA Button */}
        {/* <Box sx={{ textAlign: "center", mt: { xs: 4, md: 5 } }}>
          <Button
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            sx={{
              bgcolor: "var(--primary)",
              color: "#fff",
              px: { xs: 4, md: 6 },
              py: { xs: 1.2, md: 1.5 },
              borderRadius: "14px",
              textTransform: "none",
              fontWeight: 700,
              fontSize: { xs: "0.9rem", md: "1rem" },
              boxShadow: "var(--shadow-orange)",
              "&:hover": {
                bgcolor: "var(--primary-dark)",
                transform: "translateY(-3px)",
                boxShadow: "var(--shadow-orange-hover)",
              },
              transition: "all 0.3s ease",
            }}
          >
            See Live Demo
          </Button>
        </Box> */}
      </Container>
    </Box>
  );
};

export default HowItWorks;
